import { trackBibleView } from "./analytics";
import { getCachedVerse, setCachedVerses } from "./bible_cache";
import {
	bibleNames as BIBLE_NAMES,
	resolveBookId,
	resolveBookName,
} from "./bible_name";
import { concordance } from "./concordance";
import { floatingUpButton } from "./floating_button";
import { readMenuConfig } from "./menu_config";
import { checkIsMobile, RESIZE_CUSTOM } from "./resize-detection";
import { responsiveTable } from "./set-responsive-table";

const DEFAULT_BIBLE_LANGUAGE = "en";

// Track in-flight fetches to prevent duplicate requests for the same chapter
const fetchInProgress = new Map<string, Promise<void>>();

/**
 * Normalizes language/country codes:
 * - English variants map to "en" (no filename suffix)
 * - Everything else becomes UPPERCASE (used as filename suffix + cache namespace)
 */
function normalizeBibleLanguage(lang: string): string {
	const trimmed = (lang || "").trim();
	if (!trimmed) return DEFAULT_BIBLE_LANGUAGE;

	const upper = trimmed.toUpperCase();
	if (upper === "EN" || upper === "EN-US" || upper === "EN-GB") {
		return DEFAULT_BIBLE_LANGUAGE;
	}
	return upper;
}

function getSelectedBibleLanguage(): string {
	const config = readMenuConfig();
	return normalizeBibleLanguage(config.language);
}

function getLanguageSuffix(lang: string): string {
	const normalized = normalizeBibleLanguage(lang);
	if (normalized === DEFAULT_BIBLE_LANGUAGE) return "";
	return `_${normalized}`;
}

function getChapterFileName(bookChapter: string, lang: string): string {
	// Example:
	// - lang=en  => "John 1.html"
	// - lang=IDN => "John 1_IDN.html"
	const suffix = getLanguageSuffix(lang);
	return `${bookChapter}${suffix}.html`;
}

function getChapterHref(book: string, chapter: string, lang: string): string {
	const file = getChapterFileName(`${book} ${chapter}`, lang);
	return `/commented_bible/${encodeURIComponent(file)}`;
}

function getVerseStorageKey(
	bookname: string,
	chapter: string,
	verse: string,
	lang: string,
): string {
	// Namespace cache by language so switching languages doesn't mix verses:
	//   "en|John 1:3"
	//   "IDN|John 1:3"
	const normalizedLang = normalizeBibleLanguage(lang);
	return `${normalizedLang}|${bookname} ${chapter}:${verse}`;
}

// @ts-ignore: Named capturing groups are available in ES2018 or later
const REGEX = /(?:\?|&)(?<name>[^=&]*)=(?<value>[^&;]*)/g;

const isCharANumber = (c: string): boolean => !isNaN(Number(c));
const isCharNonNumber = (c: string): boolean => !isCharANumber(c);

function stripLegacyVersionSuffix(input: string): string {
	return input
		.replace(/;+\s*version\s*=\s*[^;]*;*/gi, "")
		.replace(/;+$/g, "")
		.trim();
}

function normalizeQueryString(search: string): string {
	// Some legacy URLs use semicolons as separators or terminators:
	//   ?search=Psalm%206;version=160;
	// Normalize to a standard query string for parsing.
	return search.replace(/;/g, "&");
}

function checkWholeBook(): string | null {
	let book: string | null = null;

	// REGEX is global, so we must reset between calls.
	REGEX.lastIndex = 0;

	const normalizedSearch = normalizeQueryString(window.location.search);

	while (true) {
		const match = REGEX.exec(normalizedSearch);
		if (match === null) break;
		if (match.groups?.name === "search") {
			const isWholeBook = !match.groups.value.includes(":");
			if (isWholeBook) {
				const rawPossibleBook = decodeURIComponent(
					decodeURIComponent(match.groups.value),
				);
				const possible_book = stripLegacyVersionSuffix(rawPossibleBook);

				const resolvename = resolveBookName(possible_book, BIBLE_NAMES);
				const tokens = resolvename.trim().split(/\s+/);
				const chapter = tokens[tokens.length - 1];
				if (tokens.length >= 2 && chapter) {
					// Check if resolved book is a valid book with chapter-ish last token
					book = resolvename;
				}
				console.log("which book >>>", book);
				console.log("which match.groups >>>", match.groups);
				break;
			}
		}
	}
	return book;
}

const capitalize = (w: string): string =>
	w[0].toUpperCase() + w.slice(1).toLowerCase();

const normalizeBookname = (bookname: string): string => {
	const matchResult =
		bookname
			.replace(/\s+/g, "")
			.match(/(\d?[A-Za-z]+)(\d+):(\d+)(?:-(\d+))?/) ?? [];

	if (matchResult.length > 0) {
		const [_, maybeBook] = matchResult;
		// Book may start with number
		const bookMatch = maybeBook.match(/^(\d+)?\s*([a-zA-Z]+)\s*(\d+)?$/);
		if (bookMatch) {
			const [, first, second] = bookMatch;
			return first ? first + " " + capitalize(second) : capitalize(second);
		}
	}

	const bookMatch = bookname.match(/^(\d+)?\s*([a-zA-Z]+)\s*(\d+)?$/);
	if (bookMatch) {
		const [, first, second, third] = bookMatch;
		return `${first || ""} ${capitalize(second)} ${third || ""}`.trim();
	}

	return bookname;
};

function getOneBook(
	bookname: string,
	chapter: string,
	lang: string,
): Promise<void> {
	// Memoization: track in-flight fetches to avoid duplicate requests
	const fetchKey = `${lang}|${bookname} ${chapter}`;
	if (fetchInProgress.has(fetchKey)) {
		return fetchInProgress.get(fetchKey)!;
	}

	// Fetch and store the books into name chapter
	const domain = window.location.origin;
	const capitalizeWord = bookname[0].toUpperCase() + bookname.slice(1);
	const bookChapter = `${capitalizeWord} ${chapter}`;

	const normalizedLang = normalizeBibleLanguage(lang);

	const baseFile = `${bookChapter}.html`;
	const langFile = getChapterFileName(bookChapter, normalizedLang);

	const baseUrl = `${domain}/commented_bible/${encodeURIComponent(baseFile)}`;
	const langUrl = `${domain}/commented_bible/${encodeURIComponent(langFile)}`;

	// IMPORTANT:
	// If the user selected a non-English language, do NOT fall back to the English chapter,
	// otherwise we'd cache English under the localized key namespace (e.g. "IDN|John 1:3").
	const tryUrls =
		normalizedLang === DEFAULT_BIBLE_LANGUAGE ? [baseUrl] : [langUrl];

	const fetchPromise = new Promise<void>((resolve) => {
		(async () => {
			let html: string | null = null;

			for (const url of tryUrls) {
				try {
					const res = await fetch(url);
					if (!res.ok) {
						continue;
					}
					html = await res.text();
					break;
				} catch {}
			}

			if (!html) {
				if (normalizedLang !== DEFAULT_BIBLE_LANGUAGE) {
					// Missing localized chapter: return empty (no cache write).
					console.info("[bible_group] missing localized chapter; skipping", {
						bookChapter,
						lang: normalizedLang,
						url: langUrl,
					});
				} else {
					console.warn("[bible_group] failed to fetch chapter for", {
						bookChapter,
						lang: normalizedLang,
						tryUrls,
					});
				}
				resolve();
				return;
			}

			const parser = new DOMParser();
			const doc = parser.parseFromString(html, "text/html");
			const verses = Array.from(
				doc.querySelectorAll<HTMLElement>(".style0"),
			).filter((v) => v.parentElement?.textContent?.length! > 10);

			console.log(`[bible_group] Extracting verses from ${verses.length} .style0 elements for ${bookChapter} (${normalizedLang})`);

		const entries: Array<{ key: string; html: string }> = [];
		for (const el of verses) {
			const elText = el.textContent ?? "";

			// Extract verse number - handle 4 cases:
			// Case 1: Number BEFORE sup in element text (e.g., "1<sup>...</sup>有一条路")
			// Case 2: Number INSIDE sup (e.g., "<sup>8</sup>有一条路")
			// Case 3: Number with nested elements in sup (e.g., "<sup><strong></strong>12</sup>")
			// Case 4: Number in parent element (German format: <p>1<span class="style0">...text...</span></p>)

			let vNumber: string | undefined;

			// Case 1: Number before sup in element text
			vNumber = elText.match(/^(\d+)/)?.[1];

			// Cases 2 & 3: Number inside sup tag (strip nested HTML elements)
			if (!vNumber) {
				const sup = el.querySelector("sup");
				if (sup) {
					// sup.textContent includes all nested text, so "8<strong></strong>" becomes "8"
					const supText = sup.textContent?.trim() ?? "";
					vNumber = supText.match(/^(\d+)/)?.[1];
				}
			}

			// Case 4: Number in parent element (German and some other translations)
			if (!vNumber) {
				const parent = el.parentElement;
				if (parent) {
					// Get the text content of parent, but also check direct text before the span
					const parentText = parent.textContent ?? "";
					// Try to find a number at the start of parent's text (before any HTML content)
					const parentMatch = parentText.match(/^(\d+)/);
					if (parentMatch) {
						vNumber = parentMatch[1];
					}
				}
			}

			if (!vNumber) {
				console.warn(
					`[bible_group] Could not extract verse number for element:`,
					{ elText: elText.slice(0, 50) },
				);
				continue;
			}

				entries.push({
					key: getVerseStorageKey(bookname, chapter, vNumber, normalizedLang),
					html: el.innerHTML,
				});
			}

			if (entries.length === 0) {
				console.warn("[bible_group] No verses extracted from chapter:", {
					bookChapter,
					lang: normalizedLang,
					url: tryUrls,
				});
			} else {
				console.log(
					`[bible_group] Cached ${entries.length} verses for ${bookChapter} (${normalizedLang})`,
				);
			}

			await setCachedVerses(entries);
			resolve();
		})().catch(() => resolve());
	});

	// Store in map and return
	fetchInProgress.set(fetchKey, fetchPromise);
	fetchPromise.finally(() => {
		fetchInProgress.delete(fetchKey);
	});
	return fetchPromise;
}

async function getFromCache(
	bookname: string,
	chapter: string,
	verse: string,
	lang: string,
): Promise<string | null> {
	const key = getVerseStorageKey(bookname, chapter, verse, lang);
	console.log("Get from cache:", key);
	const verseContent = await getCachedVerse(key);
	console.log(`Found ${key}`, verseContent);
	return verseContent;
}

interface VerseRequest {
	book: string;
	chapter: number;
	verse: number;
}

function getBookWithVerses({
	book,
	chapter,
	from,
	to,
	lang: langParam,
}: {
	book: string;
	chapter: string;
	from: number;
	to: number;
	lang?: string;
}): void {
	const lang = normalizeBibleLanguage(langParam ?? getSelectedBibleLanguage());

	const requestVerses: VerseRequest[] = [];
	const chapterNum = Number(chapter);
	from = Number(from);
	to = Number(to);
	for (let i = from; i <= to; i++) {
		requestVerses.push({ book, chapter: chapterNum, verse: i });
	}

	const groupBible = document.getElementById("groupBible");

	const div = !book ? prevBookRequest : document.createElement("div");
	if (!div) {
		console.error("div is not found");
		return;
	}
	div.className = "section";
	prevBookRequest = div;
	groupBible?.appendChild(div);

	// ==== Title
	const span = document.createElement("span");
	span.className = "bold";
	span.textContent = `${book} ${chapter}:${from}`;
	if (to && from !== to) {
		span.textContent += `-${to}`;
	}
	div.appendChild(span);

	// ==== Link
	const p = document.createElement("p");
	const a = document.createElement("a");
	a.href = getChapterHref(book, chapter, lang);
	a.textContent = `Display Chapter and Footnotes`;
	a.className = "display-chapter";
	p.appendChild(a);
	div.appendChild(p);

	// === Content
	for (const v of requestVerses) {
		void (async () => {
			const cacheKey = getVerseStorageKey(v.book, v.chapter.toString(), v.verse.toString(), lang);
			
			const found = await getFromCache(
				v.book,
				v.chapter.toString(),
				v.verse.toString(),
				lang,
			);
			if (found) {
				const p = document.createElement("p");
				p.innerHTML = found;
				div.appendChild(p);
				return;
			}

			// Not in cache, try to fetch the chapter
			await getOneBook(v.book, v.chapter.toString(), lang);

			const foundAfter = await getFromCache(
				v.book,
				v.chapter.toString(),
				v.verse.toString(),
				lang,
			);
			if (foundAfter) {
				const p = document.createElement("p");
				p.innerHTML = foundAfter;
				div.appendChild(p);
			} else {
				// Debug: log when verse still not found after fetch attempt
				console.warn(
					`[bible_group] Verse not found after fetch attempt:`,
					cacheKey,
				);
			}
		})().catch((err) => {
			console.error(`[bible_group] Error processing verse:`, v, err);
		});
	}
}

let prevBookRequest: HTMLElement | null = null;

function harvestBook(): void {
	// REGEX is global, so we must reset between calls.
	REGEX.lastIndex = 0;

	const selectedLang = getSelectedBibleLanguage();
	const normalizedSearch = normalizeQueryString(window.location.search);

	while (true) {
		const match = REGEX.exec(normalizedSearch);
		if (match === null) break;

		if (match.groups?.name === "name") {
			// For 'name', we have to refetch from archive.org
			const rawValue = decodeURIComponent(
				decodeURIComponent(match.groups.value),
			);
			const value = stripLegacyVersionSuffix(rawValue);

			const name = value.replace(
				/[^a-zA-Z0-9\s-]/g,
				"",
			) as keyof typeof concordance;
			const hashWithoutHash = window.location.hash.substring(1);
			const verses = concordance[name];
			if (verses?.length > 0) {
				const newUrl =
					"/commented_bible/?concordance=" +
					name +
					"&search=" +
					verses +
					(hashWithoutHash ? "#" + hashWithoutHash : "");
				window.location.href = newUrl;
			}
			return;
		}
		if (match.groups?.name === "search") {
			// Book with specific verse
			// Convert search string to object query
			const rawValue = decodeURIComponent(
				decodeURIComponent(match.groups.value),
			);
			const value = stripLegacyVersionSuffix(rawValue);

			let prevBook = "";
			let prevChapter: string | null = null;
			value.split(",").forEach((value) => {
				let attempt = 0;
				function formatBookName(str: string): {
					book?: string;
					chapter?: string;
					from?: string;
					to?: string;
				} {
					if (attempt >= 10) return {};
					attempt++;
					const isNumberRange = (str: string) =>
						/^\d+-\d+$/.test(str.replace(/[^0-9a-zA-Z-]/g, ""));
					const isNumber = (str: string) => /^\d+$/.test(str);
					const isBookOnly = (str: string) =>
						/^\s*(\d+)?\s*([a-zA-Z]+)\s*[^0-9]*$/.test(str);

					// Clear special character at the end of words
					str = str.replace(/[;._]$/, "");

					// Just a book without a chapter
					if (isBookOnly(str)) {
						const [, number, book] =
							str.match(/^\s*(\d+)?\s*([a-zA-Z]+)\s*[^0-9]*$/) || [];
						prevBook = `${number || ""} ${book || ""}`.trim();
						return {};
					}

					// Just number
					if (isNumberRange(str) || isNumber(str)) {
						return formatBookName(`${prevBook} ${prevChapter}:${str}`.trim());
					}

					const matchResult =
						str
							.replace(/\s+/g, "")
							.match(/^\s*(\d+)?\s*([A-Za-z]+)\s*(\d+):(\d+)(?:-(\d+))?\s*$/) ||
						[];
					const [_, number, maybeBook, chapter, from, to] = matchResult;

					// Maybe 'str' is just a verse without book name
					if (!maybeBook) {
						return formatBookName(prevBook + " " + str);
					}

					// If no book found, use prevBook
					const book = `${number || ""} ${maybeBook}`.trim() || prevBook;

					// Store book to prevBook
					prevBook = book;
					prevChapter = chapter;
					return { book, chapter, from, to };
				}

				const v = value.trim();
				const { book, chapter, from, to } = formatBookName(v);
				if (!book || !chapter || !from) return;

				if (!document.body.querySelector(".header")) {
					// Add header
					const groupBible = document.getElementById("groupBible");
					const docWrapper = document.createElement("div");
					if (groupBible) {
						docWrapper.appendChild(groupBible);
						document.body.appendChild(docWrapper);
						if (!docWrapper.querySelector(".header")) {
							// Header
							const gotoBibleIndex = document.createElement("a");
							gotoBibleIndex.href = "/commented_bible";
							gotoBibleIndex.textContent = "Go To Bible Index Page";
							gotoBibleIndex.classList.add("bible-index");
							const header = document.createElement("div");
							header.classList.add("header");
							header.appendChild(gotoBibleIndex);
							docWrapper.prepend(header);
						}
					}
				}

				getBookWithVerses({
					book: resolveBookName(book, BIBLE_NAMES),
					chapter,
					from: Number(from),
					to: Number(to ?? from),
					lang: selectedLang,
				});
			});
		}
	}
}

function emptyPageWithMessage(message: string): void {
	console.log("before empty asdf");
	Array.from(document.body.children).forEach((el) => {
		// Keep global controls: theme toggle + float button
		if (
			el instanceof HTMLDivElement &&
			(el.classList.contains("theme-toggle") ||
				el.classList.contains("float-btn-container"))
		) {
			return;
		}
		if (!(el instanceof HTMLScriptElement)) {
			el.remove();
		}
	});
	console.log("empty done");
	const div = document.createElement("div");
	div.textContent = message;
	document.body.appendChild(div);

	floatingUpButton();
	responsiveTable();
}
function dedupeHeadersKeepLast(): void {
	const headers = Array.from(
		document.querySelectorAll<HTMLDivElement>("div.header"),
	);
	if (headers.length <= 1) return;

	// Remove earlier duplicates; keep the last one (matches requested behavior).
	for (let i = 0; i < headers.length - 1; i++) {
		headers[i]?.remove();
	}
}

export function mainBible() {
	if (!window.location.pathname.includes("/commented_bible")) {
		return;
	}

	// Some generated/translated chapter pages can include duplicate `.header` blocks.
	// Keep the last one (usually the localized/most-correct header) and remove the rest.
	dedupeHeadersKeepLast();

	const query = normalizeQueryString(window.location.search);

	// Empty view if there is a search
	const allowedSearchTerm = ["search", "name", "chapter", "verse", "book_id"];
	const hasSearch = allowedSearchTerm.some((term) => query.includes(term));
	if (!hasSearch) {
		// MAIN PAGE
		floatingUpButton();
		window.addEventListener(RESIZE_CUSTOM, onMainBibleResize);
		onMainBibleResize();
		async function onMainBibleResize() {
			const isMobile = checkIsMobile();
			if (isMobile) {
				responsiveTable();
				// for mobile. remove inline style
				document
					.querySelector('[style="margin-left: 5.3in"]')
					?.removeAttribute("style");
				document
					.querySelector('img[width="456"]')
					?.setAttribute("width", "95vw");
				console.log(document.querySelector('[style="margin-left: 5.3in"]'));
				trimBookName(document.body);
			}
		}

		return;
	}

	// book_id support (e.g., ?book_id=54&chapter=4&verse=6)
	const urlParams = new URLSearchParams(query);
	const bookIdParam = urlParams.get("book_id");
	if (bookIdParam) {
		const bookName = resolveBookId(bookIdParam);
		if (bookName) {
			const chapterParam = urlParams.get("chapter");
			const verseParam = urlParams.get("verse");
			const hashWithoutHash = window.location.hash.substring(1);

			// If chapter/verse are present, reuse the search-based flow so we only pull the requested verses.
			if (chapterParam || verseParam) {
				const nextParams = new URLSearchParams(query);
				nextParams.delete("book_id");
				nextParams.delete("chapter");
				nextParams.delete("verse");

				let searchValue = bookName;
				if (chapterParam) {
					searchValue += ` ${chapterParam}`;
					if (verseParam) {
						searchValue += `:${verseParam}`;
					}
				} else if (verseParam) {
					// Uncommon: verse without chapter; still forward it.
					searchValue += ` ${verseParam}`;
				}
				nextParams.set("search", searchValue);

				// Manually encode to keep spaces as %20 (not '+') and keep ':' unencoded.
				const queryString = Array.from(nextParams.entries())
					.map(([key, value]) => {
						if (key === "search") {
							const encodedValue = encodeURIComponent(value).replace(
								/%3A/gi,
								":",
							);
							return `${encodeURIComponent(key)}=${encodedValue}`;
						}
						return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
					})
					.join("&");

				const hashPart = verseParam
					? `#${verseParam}`
					: hashWithoutHash
						? `#${hashWithoutHash}`
						: "";

				window.location.href = `/commented_bible/?${queryString}${hashPart}`;
				return;
			}

			// No chapter/verse: show the whole book
			const hashPart =
				hashWithoutHash && !hashWithoutHash.startsWith("#")
					? `#${hashWithoutHash}`
					: "";
			window.location.href = `/commented_bible/${bookName}.html${hashPart}`;
			return;
		}
	}

	const whichWholeBook = checkWholeBook();
	if (whichWholeBook) {
		emptyPageWithMessage("Loading ...");
		console.log("What is the book", whichWholeBook);

		const resolveName = resolveBookName(
			stripLegacyVersionSuffix(capitalize(whichWholeBook)),
			BIBLE_NAMES,
		);

		// ResolveName is a Bible book, but it's possible that the book has a bug
		const hashWithoutHash = window.location.hash.substring(1);

		window.location.href =
			`/commented_bible/${normalizeBookname(resolveName)}.html` +
			(hashWithoutHash ? "#" + hashWithoutHash : "");
		return;
	}

	emptyPageWithMessage("");
	const groupBible = document.createElement("div");
	groupBible.id = "groupBible";
	groupBible.className = "groupBible";
	document.body.appendChild(groupBible);
	harvestBook();

	const urlSearchParams = new URLSearchParams(query);
	console.log(urlSearchParams);
	console.log(query);

	// Record which generated commented-bible content was actually viewed.
	trackBibleView({
		concordance: urlSearchParams.get("concordance") ?? undefined,
		search: urlSearchParams.get("search") ?? undefined,
	});
}

function trimBookName(el: HTMLElement) {
	Array.from(document.querySelectorAll("#booklist td:first-child")).forEach(
		(el) => {
			console.log(el);
			if (el.className === "style4") return;
			const firstChild = el.firstElementChild;
			if (firstChild?.textContent) {
				const firstChar = Number(firstChild.textContent[0]);
				const isFirstCharANumber = firstChar >= 0;
				const sliceAmt = isFirstCharANumber ? 5 : 3;
				firstChild.textContent = firstChild.textContent?.slice(0, sliceAmt);
			}
		},
	);
}
